import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { requestOtp, OtpPromptCancelledError, clearCachedOtp } from "@/lib/otp-prompt";
import { getAccessToken, getRefreshToken, refreshRequest, setTokens, clearTokens } from "@/lib/auth";
import { getStoredCurrentEnvironment } from "@/lib/environment";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ---------------------------------------------------------------------------
// 1. JWT Bearer 注入
//    优先读 admin_jwt_access_token；fallback quantum_token（legacy）
// ---------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  const currentEnv = getStoredCurrentEnvironment();
  if (currentEnv?.id && !config.headers["X-Admin-Env-Id"]) {
    config.headers["X-Admin-Env-Id"] = currentEnv.id;
  }
  return config;
});

// ---------------------------------------------------------------------------
// 2. Idempotency-Key 自动注入（写操作）
//    - 仅 POST / PUT / PATCH / DELETE
//    - 已显式传入 Idempotency-Key 时不覆盖
//    - 使用 crypto.randomUUID 生成 v4 UUID
// ---------------------------------------------------------------------------
const WRITE_METHODS = ["post", "put", "patch", "delete"] as const;

function isWriteMethod(method?: string): boolean {
  if (!method) return false;
  return (WRITE_METHODS as readonly string[]).includes(method.toLowerCase());
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments lacking crypto.randomUUID（不应触发，仅兜底）
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!isWriteMethod(config.method)) return config;
  if (!config.headers) return config;
  if (!config.headers["Idempotency-Key"]) {
    config.headers["Idempotency-Key"] = generateIdempotencyKey();
  }
  return config;
});

// ---------------------------------------------------------------------------
// 3. 高危操作 OTP 自动 prompt
//    匹配清单见 HIGH_RISK_PATTERNS。任一匹配项 + 写方法 = 触发 prompt。
//    用户已显式传入 X-OTP-Code 时不再 prompt。
// ---------------------------------------------------------------------------
// risk confirmation 已全局下线
const HIGH_RISK_OPERATIONS: readonly { pattern: string; operationKey: string }[] = [];

function getRiskOperationKey(url?: string | null): string | null {
  if (!url) return null;
  const match = HIGH_RISK_OPERATIONS.find((item) => url.includes(item.pattern));
  if (!match) return null;
  if (url.includes("membership")) return "user.membership.write";
  if (url.includes("/adjust-points")) return "glowpic.user.points.write";
  return match.operationKey;
}

export function isHighRiskOperation(url?: string | null): boolean {
  return getRiskOperationKey(url) !== null;
}

async function requestRiskConfirmation(config: InternalAxiosRequestConfig): Promise<string | null> {
  const currentEnv = getStoredCurrentEnvironment();
  if (!currentEnv?.is_prod) return null;
  const operationKey = getRiskOperationKey(config.url) ?? "unknown";
  const confirmed = window.confirm(`你正在 ${currentEnv.display_name} 执行高危操作，请确认继续。`);
  if (!confirmed) {
    throw new OtpPromptCancelledError();
  }
  const apiBaseUrl = import.meta.env.VITE_API_URL || "";
  const response = await axios.post(
    `${apiBaseUrl}/quantum/business/risk/confirmations`,
    {
      env_id: currentEnv.id,
      operation_key: operationKey,
    },
    {
      headers: {
        "Content-Type": "application/json",
        ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
      },
    }
  );
  return response.data?.data?.confirmation_token ?? null;
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (!isWriteMethod(config.method)) return config;
  if (!config.headers) return config;
  if (config.headers["X-OTP-Code"]) return config; // 调用方已显式提供

  if (!isHighRiskOperation(config.url)) return config;

  if (!config.headers["X-Risk-Confirmation-Token"]) {
    const token = await requestRiskConfirmation(config);
    if (token) {
      config.headers["X-Risk-Confirmation-Token"] = token;
    }
  }

  try {
    const code = await requestOtp({
      endpoint: config.url || undefined,
      title: "高危操作 — 请输入 OTP 验证码",
      description: "此操作涉及结算 / 资金 / 配置变更，需要二次验证。",
    });
    config.headers["X-OTP-Code"] = code;
  } catch (err) {
    if (err instanceof OtpPromptCancelledError) {
      // 用户主动取消 → 阻断请求
      throw err;
    }
    throw err;
  }
  return config;
});

// ---------------------------------------------------------------------------
// 4. 响应拦截：
//    - 401 → 自动尝试 refresh access_token；成功重试原请求，失败清 token 跳 /login
//    - 403 + OTP 无效 → 清缓存的 OTP（下次重新输入）
//
// refresh 失败定义：refresh_token 不存在 / refresh 接口返回非 2xx / refresh 接口本身 401
// 并发请求保护：refresh 期间所有其它 401 共享同一 refresh Promise，避免重复刷
// ---------------------------------------------------------------------------
const REFRESH_ENDPOINT_PATH = "/quantum/business/auth/refresh";

type RetriableConfig = AxiosRequestConfig & { _retry?: boolean };
let inflightRefresh: Promise<void> | null = null;

async function performRefreshOnce(): Promise<void> {
  if (!inflightRefresh) {
    inflightRefresh = (async () => {
      try {
        const newTokens = await refreshRequest();
        setTokens(newTokens);
      } finally {
        // 无论成败，下次 401 都重新尝试（成功 → 用新 token；失败 → 已被外层清除）
        inflightRefresh = null;
      }
    })();
  }
  return inflightRefresh;
}

function redirectToLogin(): void {
  clearTokens();
  localStorage.removeItem("quantum_admin");
  if (typeof window !== "undefined" && window.location) {
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error?.response?.status;
    const originalConfig = error?.config as RetriableConfig | undefined;
    const url = originalConfig?.url || "";

    // 403: OTP 校验失败兜底（保留原逻辑）
    if (status === 403) {
      const errorCode = (error?.response?.data as { error?: { code?: string } } | undefined)
        ?.error?.code;
      const errorMsg =
        (error?.response?.data as { error?: { message?: string }; message?: string } | undefined)
          ?.error?.message ||
        (error?.response?.data as { message?: string } | undefined)?.message;
      if (
        (typeof errorCode === "string" && errorCode.toUpperCase().includes("OTP")) ||
        (typeof errorMsg === "string" && /otp/i.test(errorMsg))
      ) {
        clearCachedOtp();
      }
      return Promise.reject(error);
    }

    if (status !== 401 || !originalConfig) {
      return Promise.reject(error);
    }

    // refresh 接口本身返回 401 → 直接登出，不递归
    if (url.includes(REFRESH_ENDPOINT_PATH)) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // 其他 auth 端点（login / verify-code 等）401 直接冒泡，不走 refresh 重试
    if (url.includes("/quantum/business/auth/")) {
      return Promise.reject(error);
    }

    // 已重试过一次 → 不再 loop
    if (originalConfig._retry) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // 无 refresh_token 直接跳登录
    if (!getRefreshToken()) {
      redirectToLogin();
      return Promise.reject(error);
    }

    originalConfig._retry = true;
    try {
      await performRefreshOnce();
    } catch (refreshErr) {
      redirectToLogin();
      return Promise.reject(refreshErr);
    }

    // 用新 access_token 重试
    const newToken = getAccessToken();
    if (originalConfig.headers && newToken) {
      (originalConfig.headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
    }
    return api.request(originalConfig);
  }
);

export { api };
export default api;
